import { cleanEvidenceAnnotations } from '@/mocks/realResultAdapter';
import {
  normalizeLeadType,
  type PreliminaryLead,
  type PreliminaryMaturity,
} from '@/mocks/preliminaryLeads';
import type { V3Lead, V3LeadsPayload } from '@/services/enterpriseApi';
import { materialsFromMissingEvidence } from '@/utils/companyMaterialRequirements';

/** 无事件型事实时的触发槽软提示（禁止套标题；禁止复用到已知事实槽） */
export const SOFT_TRIGGER_NO_EVENT =
  '暂无明确的公开经营事件，建议结合登记信息与待核验项核实';

/** 已知事实槽空态（与触发软提示分离，避免双槽同文） */
export const SOFT_KNOWN_FACTS_EMPTY = '暂无独立的已知公开事实';

/** v3 字段码 → 人读名（含待核验句中裸露字段码） */
const FIELD_LABELS: Record<string, string> = {
  insured_count: '从业人数',
  staff_size: '人员规模',
  industry_class: '所属行业',
  main_business_or_scope: '经营范围',
  establishment_date: '成立日期',
  taxpayer_type: '纳税人类型',
  taxpayer_id: '纳税人识别号',
  legal_representative: '法定代表人',
  frname: '法定代表人',
  registered_capital: '注册资本',
  registered_capital_raw: '注册资本',
  registration_status: '登记状态',
  enterprise_type: '企业类型',
  enttype: '企业类型',
  entstatus: '登记状态',
  credit_rating: '纳税信用',
  province: '登记省份',
  enterprise_name: '企业名称',
  uscc: '统一社会信用代码',
  reg_capital: '注册资本',
  reg_status: '登记状态',
  business_scope: '经营范围',
  taxable_income: '应纳税所得额',
  eit_rate: '企业所得税税率',
  eit_relief: '企业所得税减免',
  tax_rate: '税率',
  tax_class_code: '税收分类编码',
  tax_class: '税目分类',
  tax_period_start: '税款所属期起',
  tax_period_end: '税款所属期止',
  tax_period: '税款所属期',
  import_export_flag: '进出口标识',
  vat_paid: '实缴增值税',
  consumption_tax_paid: '实缴消费税',
  exempt_offset_refund: '免抵退税额',
  collection_method: '征收方式',
  asset_type: '资产类型',
  inventory_qty: '库存数量',
  unit_cost: '单位成本',
  actual_cost: '实际成本',
  contract_amount_gross: '合同含税金额',
  contract_subject: '合同标的',
  invoice_date: '开票日期',
  invoice_code: '发票代码',
  invoice_number: '发票号码',
  invoice_type: '发票类型',
  invoice_amount: '发票金额',
  invoice: '发票',
  invoicing: '开票',
  direct_holding_pct: '直接持股比例',
  shareholding_ratio: '持股比例',
  shareholder_name: '股东名称',
  actual_controller_name: '实际控制人',
  actual_controller_direct_ratio: '直接持股比例',
  actual_controller_voting_ratio: '表决权比例',
  shareholder_id: '股东标识',
  former_names: '曾用名',
  annual_report_year: '年报年度',
  approval_date: '核准日期',
  days_since_approval: '距核准天数',
  industry_license_event: '行业许可事项',
  qualification_expiry_event: '资质到期事项',
  scope_change_event: '经营范围变更',
  equity_change_event: '股权变更',
  charter_change_event: '章程变更',
  event_stage: '事项阶段',
  official_obtained: '已取得官方资料',
  required_status: '要求状态',
  item_id: '明细项编号',
  item_name: '明细项名称',
  topic_id: '主题编号',
  query_basis: '查询依据',
  before: '变更前',
  after: '变更后',
  reason: '原因',
  amount: '金额',
  count: '数量',
  item: '事项',
  risk: '风险',
  type: '类型',
  // 公示联系 / 出资
  phone: '公示电话',
  tel: '公示电话',
  telephone: '公示电话',
  mobile: '手机号',
  contact_phone: '公示电话',
  contact_tel: '公示电话',
  contact_number: '公示电话',
  annual_report_phone: '年报公示电话',
  report_phone: '年报公示电话',
  subscribed_capital: '认缴出资额',
  paid_in_capital: '实缴出资额',
  contribution_amount: '出资金额',
  contribution_ratio: '出资比例',
  capital_contribution: '出资信息',
  // 企业画像标签（推理常写 tags 含「…」）
  tags: '企业标签',
  tag: '企业标签',
  enterprise_tags: '企业标签',
  company_tags: '企业标签',
};

/** 截断的英文词根 → 中文（invoic… / tax_cl…） */
const ELLIPSIS = '(?:…|\\.\\.\\.|…)';
const TRUNCATED_STEMS: Array<[RegExp, string]> = [
  [new RegExp(`\\binvoic[a-z_]*${ELLIPSIS}?`, 'gi'), '发票'],
  [new RegExp(`\\btax_class[a-z_]*${ELLIPSIS}?`, 'gi'), '税收分类编码'],
  [new RegExp(`\\btax_rate[a-z_]*${ELLIPSIS}?`, 'gi'), '税率'],
  [new RegExp(`\\bcollection[a-z_]*${ELLIPSIS}?`, 'gi'), '征收方式'],
  [new RegExp(`\\basset_type[a-z_]*${ELLIPSIS}?`, 'gi'), '资产类型'],
];

const INLINE_KEY_RE =
  /\b([A-Za-z][A-Za-z0-9_]*(?:\[[0-9]+\])?(?:\.[A-Za-z][A-Za-z0-9_]*)*)\s*=\s*([^，,；;\n）)]+)/g;

function humanizeFieldKey(key: string): string {
  const k = key.trim().toLowerCase();
  if (FIELD_LABELS[k]) return FIELD_LABELS[k];
  if (/^changes\[\d+]$/i.test(k)) return '工商变更';
  return '';
}

/** 去掉括号内证据/来源噪声 */
function stripNoiseParens(text: string): string {
  let s = text;
  for (let i = 0; i < 8; i += 1) {
    const next = s.replace(/[（(][^（）()]*[）)]/g, (block) => {
      if (
        /FACT-|AUD-|证据\s*E\d|\bE[1-4]\b|@\s*[\w.[\]]+|来源|basic\.|qualification\.|changes\[/i.test(
          block,
        )
      ) {
        return '';
      }
      // 底稿模块来源标签，不对客户展示（·变更 / ，变更 等）
      if (/^[（(]\s*事件时间轴[^）)]*[）)]$/.test(block.trim())) {
        return '';
      }
      if (/^[（(]\s*[A-Za-z][A-Za-z0-9_.[\]]*\s*[）)]$/.test(block.trim())) {
        return '';
      }
      // （ENT-0307…）仅内部主体 ID
      if (/^[（(]\s*(?:ENT|LEAD|CASE|EVT|REL)-[A-Za-z0-9]+\s*[）)]$/i.test(block.trim())) {
        return '';
      }
      // (count=0) 类：先留给后续 inline 翻译，此处若整段是 key=val 则展开
      const kv = block.match(/^[（(]\s*([A-Za-z_][\w]*)\s*=\s*([^）)]+)\s*[）)]$/);
      if (kv) {
        const label = humanizeFieldKey(kv[1]);
        return label ? `（${label} ${kv[2].trim()}）` : `（${kv[2].trim()}）`;
      }
      return block;
    });
    if (next === s) break;
    s = next;
  }
  s = s.replace(
    /[，,]?\s*来源\s*AUD-[A-Za-z0-9]+(?:\s*@\s*[\w.[\]]+)?(?:\s*[，,]\s*证据\s*E\d+)?/gi,
    '',
  );
  s = s.replace(/\s*@\s*[\w.[\]]+/g, '');
  s = s.replace(/[，,]?\s*证据\s*E\d+/gi, '');
  s = s.replace(
    /[（(]\s*(?:FACT-)?-?[A-Fa-f0-9]{8,}(?:\s*[，,]\s*E\d+)?\s*[）)]/gi,
    '',
  );
  s = s.replace(/\bFACT-[A-Za-z0-9]+\b/gi, '');
  s = s.replace(/\bAUD-[A-Za-z0-9]+\b/gi, '');
  return s;
}

/**
 * 去掉国标行业门类码（C35、I65 等），只留中文业态名。
 * 例：C35 专用设备制造业 → 专用设备制造业
 */
export function humanizeIndustryLabel(text: string | null | undefined): string {
  if (!text) return '';
  let s = String(text).trim();
  s = s.replace(/[（(]\s*国民经济行业[^）)]*[）)]/g, '');
  s = s.replace(/国民经济行业\s*/g, '');
  s = s.replace(/^([A-T]\d{2}\d{0,2})\s+/i, '');
  s = s.replace(/(^|[\s·、，,/／|])([A-T]\d{2}\d{0,2})\s*/gi, '$1');
  s = s.replace(/\s*[A-T]\d{2}\d{0,2}\s*(?=[、，,/／|]|$)/gi, '');
  s = s.replace(/\s{2,}/g, ' ').replace(/^[·、，,\s/／|]+|[·、，,\s/／|]+$/g, '').trim();
  return s || String(text).trim();
}

/**
 * 句中任意 key=value / changes[n] / 孤立英文字段码 → 中文。
 * 优先处理 code（中文说明）：取括号内中文，避免「应纳税所得额（应纳税所得额）」重复乱码。
 */
export function humanizeV3InlineText(text: string): string {
  let s = text;

  // 行业类别为 C35 专用设备制造业 → 所属行业：专用设备制造业
  s = s.replace(
    /行业(?:类别|门类|分类)?[为是：:\s]*[A-T]\d{2}\d{0,2}\s*/gi,
    '所属行业：',
  );
  // 句中残留的国标行业码（后接中文业态名时剥掉）
  s = s.replace(/\b[A-T]\d{2}\d{0,2}\s+(?=[\u4e00-\u9fff]{2,})/gi, '');

  // changes[1] 2023-02-27 … → 工商变更（2023-02-27）：…
  s = s.replace(
    /\bchanges\[(\d+)\]\s*(20\d{2}-\d{2}-\d{2})?\s*/gi,
    (_m, _idx: string, date?: string) =>
      date ? `工商变更（${date}）：` : '工商变更：',
  );

  // detail2 子表路径（推理侧常把 ENTINV[8].REGCAP 等写进事实）→ 中文，避免「ENTINV[8].1000000」上屏
  s = s.replace(
    /\b(ENTINV|FRINV|SHAREHOLDER|PERSON|ALTER|HISTORY_MANAGER|AnnualReportCapital|AnnualReport|AnnualSocial|ABNORMAL)\[\d+\](?:\.[A-Za-z0-9_]+)?\b/gi,
    (_m, table: string) => {
      const map: Record<string, string> = {
        entinv: '对外投资',
        frinv: '对外投资',
        shareholder: '股东出资',
        person: '主要人员',
        alter: '工商变更',
        history_manager: '历史高管',
        annualreportcapital: '年报出资',
        annualreport: '企业年报',
        annualsocial: '年报社保',
        abnormal: '经营异常',
      };
      return map[String(table).toLowerCase()] || '登记资料';
    },
  );
  // 「对外投资记录存在 对外投资」→「存在对外投资记录」
  s = s.replace(
    /(对外投资|股东出资|工商变更|主要人员)记录存在\s*\1(?:记录)?/g,
    '存在$1记录',
  );
  s = s.replace(
    /存在\s*(对外投资|股东出资|工商变更)\s*\1(?:记录)?/g,
    '存在$1记录',
  );

  // tags 含"国家高新技术企业" → 企业标签含「国家高新技术企业」
  s = s.replace(
    /\b(?:tags?|enterprise_tags|company_tags)\s*含\s*[「"“']?([^」"”']+)[」"”']?/gi,
    (_m, body: string) => `企业标签含「${String(body).trim()}」`,
  );

  // 截断英文（invoic…）
  for (const [re, label] of TRUNCATED_STEMS) {
    s = s.replace(re, label);
  }

  // 句中 key=value（可多处）
  s = s.replace(INLINE_KEY_RE, (_m, key: string, value: string) => {
    const label = humanizeFieldKey(key);
    const v = value.trim();
    if (label) return `${label} ${v}`;
    // 未知英文字段：电话补标签；纯数字碎片丢弃，避免「028-xxx」「8.08/1.07」裸上屏
    if (/(?:\+?86[-\s]?)?(?:0\d{2,3}-?\d{7,8}|1[3-9]\d{9})/.test(v) && /^[\d+\-\s]+$/.test(v)) {
      return `公示电话 ${v}`;
    }
    if (/^[\d.\s/／、,，-]+$/.test(v)) return '';
    return v;
  });

  // ① code：中文… → 去掉英文字段码，保留冒号后说明
  s = s.replace(/\b([A-Za-z][A-Za-z0-9_]*)\s*[：:]\s*/g, (_m, code: string) => {
    const label = humanizeFieldKey(code);
    return label ? '' : '';
  });

  // ② code（中文…）→ 优先括号内说明，杜绝 code 与中文标签叠在一起
  s = s.replace(
    /\b([A-Za-z][A-Za-z0-9_]*)\s*[（(]([^）)]+)[）)]/g,
    (_m, code: string, inner: string) => {
      const body = inner.trim();
      if (/[\u4e00-\u9fff]/.test(body)) return body;
      return humanizeFieldKey(code) || body || code;
    },
  );

  // ③ code 字段 / 标准字段
  s = s.replace(
    /\b([A-Za-z][A-Za-z0-9_]*)\s*(?:标准)?字段\b/gi,
    (_m, code: string) => humanizeFieldKey(code) || '相关资料项',
  );

  // ④ 裸字段码（taxable_income、eit_rate …）→ 中文名
  const keys = Object.keys(FIELD_LABELS).sort((a, b) => b.length - a.length);
  for (const key of keys) {
    const re = new RegExp(`(?<![A-Za-z0-9_])${key}(?![A-Za-z0-9_])`, 'gi');
    s = s.replace(re, FIELD_LABELS[key]);
  }
  // 映射后仍可能是「企业标签 含"…"」→ 统一书名号
  s = s.replace(
    /企业标签\s*含\s*[「"“']?([^」"”']+)[」"”']?/g,
    (_m, body: string) => `企业标签含「${String(body).trim()}」`,
  );

  // 内部实体 / 线索 / 案件 ID（ENT-xxx、LEAD-xxx…）不上屏
  s = s.replace(/\b(?:ENT|LEAD|CASE|EVT|REL)-[A-Za-z0-9]+\b/gi, '');
  // 内部规则/资料包编号不在客户文案中展示（LEGAL-001、EQUITY-01、GOV-01…）
  s = s.replace(
    /\b[A-Z][A-Z0-9]*(?:-\d{1,4})+(?:\s*\/\s*[A-Z][A-Z0-9]*(?:-\d{1,4})+)*\b/gi,
    '',
  );
  s = s.replace(/<\/?think>/gi, '');
  s = s.replace(/\[(?:思考|推理|内部)[^\]]*\]/g, '');
  s = s.replace(/[（(][^）)]*(?:最低事实|当前缺失|闭环|minimum_documents|思考|推理)[^）)]*[）)]/gi, '');
  // 剥 ID 后空括号：公司名（）→ 公司名
  s = s.replace(/[（(]\s*[）)]/g, '');

  // ③ 仍残留的 snake_case → 「相关资料项」，避免英文上屏
  s = s.replace(/\b[a-z][a-z0-9]*(?:_[a-z0-9]+)+\b/gi, '相关资料项');
  // 残留截断英文词
  s = s.replace(/\b[a-z][a-z0-9_]{1,24}(?:…|\.\.\.)/gi, '相关资料项');

  // risk 单独出现
  s = s.replace(/(?<![A-Za-z\u4e00-\u9fff])risk(?![A-Za-z])/gi, '风险');
  s = s.replace(/风险\s+类型/g, '风险类型');
  // 连续「相关资料项」合并
  s = s.replace(/(相关资料项[、，,]?\s*){2,}/g, '相关资料项、');

  // 金额数字美化：变更前/后纯数字
  s = s.replace(
    /(变更前|变更后)\s+(\d{5,})(?!\d|元|万)/g,
    (_m, label: string, num: string) => {
      const n = Number(num);
      if (!Number.isFinite(n)) return `${label} ${num}`;
      if (n >= 10000 && n % 10000 === 0) return `${label} ${n / 10000}万元`;
      return `${label} ${num}元`;
    },
  );

  // 英文冒号统一；去掉「与 taxable_income」替换后残留的空格
  s = s.replace(/:\s*/g, '：');
  s = s.replace(/[（(]\s*[）)]/g, '');
  s = s.replace(/([与和的及、，,])\s+(?=[\u4e00-\u9fff])/g, '$1');
  s = s.replace(/\s{2,}/g, ' ');
  s = s.replace(/[，,]{2,}/g, '，');
  s = s.replace(/[；;]{2,}/g, '；');
  s = s.replace(/\s*([，,；;：])\s*/g, '$1');
  s = s.replace(/^[：:=\s，,；;]+|[：:=\s，,；;]+$/g, '').trim();
  return s;
}

/** 线索标题人读化：去掉 ENT-/LEAD- 前缀与内部编号 */
export function cleanLeadDisplayTitle(raw: string | null | undefined): string {
  let s = String(raw || '').trim();
  if (!s) return '未命名线索';
  s = s.replace(/^(?:ENT|LEAD|CASE|EVT|REL)-[A-Za-z0-9]+\s+/i, '');
  s = s.replace(/\b(?:ENT|LEAD|CASE|EVT|REL)-[A-Za-z0-9]+\b/gi, '');
  s = humanizeV3InlineText(s);
  s = s.replace(/[（(]\s*[）)]/g, '');
  s = s.replace(/\s{2,}/g, ' ').trim();
  return s || '未命名线索';
}

function normalizeCompareText(text: string): string {
  return String(text || '')
    .replace(/\s+/g, '')
    .replace(/[，,。．；;：:、]/g, '')
    .toLowerCase();
}

/**
 * composite / 推导层写入 public_facts·inferences 的元描述，不可当触发事件或机会判断。
 */
export function isV3ComposeMetaText(text: string | null | undefined): boolean {
  const s = String(text || '')
    .trim()
    // 全角数字 → 半角，便于匹配「组合自２条」
    .replace(/[０-９]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xff10 + 0x30));
  if (!s) return true;
  if (
    /组合自\s*\d+\s*条父线索|父线索的连接|须补足原证据与连接证据|连接可能揭示|由\s*\d+\s*条父线索|父线索组合|推导命题|origin_layer|parent_lead_ids|补足原证据/i.test(
      s,
    )
  ) {
    return true;
  }
  // 只要在谈「父线索/组合」且几乎没有业务实体词，一律当元文案
  if (
    /父线索|组合自|连接证据|核验命题/.test(s) &&
    !/[0-9]{4}|万元|股权|变更|挂牌|公告|物业|转让|设立|项目公司|税务|税负|发票|注册资本/.test(s)
  ) {
    return true;
  }
  return false;
}

/** 截断后去掉未闭合的中文/英文括号尾巴，避免「（须补足…」半截上屏 */
function trimUnbalancedParens(text: string): string {
  let s = text;
  const opens = (s.match(/[（(]/g) || []).length;
  const closes = (s.match(/[）)]/g) || []).length;
  if (opens <= closes) return s;
  const idxCn = s.lastIndexOf('（');
  const idxEn = s.lastIndexOf('(');
  const idx = Math.max(idxCn, idxEn);
  if (idx < 0) return s;
  s = s.slice(0, idx).replace(/[；;，,。.…]+$/g, '').trim();
  return s;
}

/**
 * 信数正式边界：主体静态属性 ≠ 触发事件。
 * 例：basic.FRNAME=李建清、ENTTYPE、REGCAPCN、tax_type_name 等。
 */
export function isStandingAttributeFact(text: string | null | undefined): boolean {
  const s = String(text || '').trim();
  if (!s) return true;
  if (
    /^(法定代表人|企业类型|纳税人类型|登记状态|注册资本|信用代码|所属行业|从业人数|经营范围|企业名称)[:：]/.test(
      s,
    )
  ) {
    return true;
  }
  if (
    /^(legal_representative|enterprise_type|taxpayer_type|registration_status|registered_capital(_raw)?|uscc|credit_code|industry_class|staff_size|main_business_or_scope)\s*[=：:]/i.test(
      s,
    )
  ) {
    return true;
  }
  // 清洗后只剩 2~4 字人名（如「李建清」）——来自 FRNAME，不是事件
  if (/^[\u4e00-\u9fff]{2,4}$/.test(s)) return true;
  // 国标行业层级、事实账本行业清单：静态登记，不是经营事件
  if (/^国标行业/.test(s) || /事实账本\s*(所属行业|industry_class)/i.test(s)) return true;
  return false;
}

const PHONE_RE = /(?:\+?86[-\s]?)?(?:0\d{2,3}-?\d{7,8}|1[3-9]\d{9})/;

/**
 * 低信息事实：裸电话、裸数字串、无中文的金额比例碎片。
 * 根因：未知英文字段码被剥掉后只剩值（如 phone=028-xxx → 028-xxx）。
 * 可作「已知事实」原料，但不得当触发事件。
 */
export function isLowSignalFact(text: string | null | undefined): boolean {
  const s = String(text || '').trim();
  if (!s) return true;
  // 纯电话
  if (new RegExp(`^${PHONE_RE.source}$`).test(s)) return true;
  // 「公示电话：028-…」仅联系方式，不是事件
  if (
    /^(?:公示|年报公示|联系)?电话[:：]?\s*/.test(s) &&
    new RegExp(`^${PHONE_RE.source}$`).test(s.replace(/^(?:公示|年报公示|联系)?电话[:：]?\s*/, ''))
  ) {
    return true;
  }
  // 纯数字 / 登记号片段
  if (/^\d{6,}$/.test(s)) return true;
  // 无中文的数字比例：8.08 / 1.0733 / 8.0005
  if (!/[\u4e00-\u9fff]/.test(s) && /^[\d.\s/／、,，-]+$/.test(s)) return true;
  // 无中文且含数字的短碎片
  if (!/[\u4e00-\u9fff]/.test(s) && /\d/.test(s) && s.length <= 48) return true;
  return false;
}

/** 不宜作为触发事件（静态属性 + 低信息碎片） */
function isUnsuitableTriggerFact(text: string): boolean {
  return isStandingAttributeFact(text) || isLowSignalFact(text);
}

const EVENT_DATE_RE = /20\d{2}-\d{2}-\d{2}/;
const EVENT_HINT_RE =
  /变更|备案|挂牌|转让|增资|减资|退出|设立|处罚|中标|迁址|经营范围|投资人|股权|章程|高管|地址|公示|核验|更正|不一致|出资/;

/** 从事实/标题中提取事件发生日（优先于 lead.period 分析期末） */
export function extractEventDate(...texts: Array<string | null | undefined>): string {
  for (const raw of texts) {
    const m = String(raw || '').match(EVENT_DATE_RE);
    if (m) return m[0];
  }
  return '';
}

/**
 * 挑选触发事件：优先带日期的工商/经营变动句，跳过 FRNAME / 裸电话等。
 * 海擎实测：public_facts[0] 常是 legal_representative=李建清，真事件在后续「2025-12-17 投资人变更」。
 */
export function pickTriggerFact(facts: string[]): string {
  const list = facts.map((f) => f.trim()).filter(Boolean);
  if (!list.length) return '';
  const ok = (f: string) => !isUnsuitableTriggerFact(f);
  const datedEvent = list.find(
    (f) => EVENT_DATE_RE.test(f) && EVENT_HINT_RE.test(f) && ok(f),
  );
  if (datedEvent) return datedEvent;
  const dated = list.find((f) => EVENT_DATE_RE.test(f) && ok(f));
  if (dated) return dated;
  const hinted = list.find((f) => EVENT_HINT_RE.test(f) && ok(f));
  if (hinted) return hinted;
  // 有中文业务句才可回退；禁止裸电话/数字顶成触发事件
  const narrative = list.find((f) => ok(f) && /[\u4e00-\u9fff]{4,}/.test(f));
  return narrative || '';
}

/**
 * 清洗 v3 public_facts。
 * 不调用 sanitizeCustomerText（会误删中文值或留下空括号）。
 */
export function cleanV3PublicFact(text: string | null | undefined): string {
  if (!text) return '';
  if (isV3ComposeMetaText(text)) return '';
  let s = String(text).trim();
  s = s.replace(/^FACT-[A-Za-z0-9]+\s+/i, '');
  // 去掉 O04 模块前缀/尾标（「事件时间轴：…」「…（事件时间轴，变更）」）
  s = s.replace(/^事件时间轴[^：:\n]{0,16}[：:]\s*/u, '');
  s = stripNoiseParens(s);
  s = s.replace(/[（(]\s*事件时间轴[^）)]*[）)]/gu, '').trim();

  // 整句 key=value：未知字段若值是电话/数字，补中文标签，避免只剩裸值
  s = s.replace(
    /^([A-Za-z][A-Za-z0-9_.[\]]*)\s*=\s*(.+)$/s,
    (_m, key: string, value: string) => {
      const label = humanizeFieldKey(key);
      const v = value.trim();
      if (label) return `${label}：${v}`;
      if (new RegExp(`^${PHONE_RE.source}$`).test(v)) return `公示电话：${v}`;
      if (/^[\d.\s/／、,，-]+$/.test(v) && !/[\u4e00-\u9fff]/.test(v)) {
        return ''; // 无标签出资比例等数字碎片直接丢弃
      }
      return v;
    },
  );

  s = humanizeV3InlineText(s);

  if (/FACT-|AUD-|\bE[1-4]\b|@[a-z]+\./i.test(s)) {
    s = humanizeV3InlineText(stripNoiseParens(s));
  }

  // 年报记录中 028-xxx、数字… → 年报公示电话
  s = s.replace(
    /^年报记录中\s*((?:\+?86[-\s]?)?(?:0\d{2,3}-?\d{7,8}|1[3-9]\d{9})).*$/i,
    '年报公示电话：$1',
  );
  // 裸电话补标签（可进已知事实，但不会进触发事件）
  if (new RegExp(`^${PHONE_RE.source}$`).test(s)) {
    s = `公示电话：${s}`;
  }

  // 过短无信息碎片
  if (/^(存续|在业|开业)$/.test(s)) s = `登记状态：${s}`;

  if (!s || !/[\u4e00-\u9fff\d]/.test(s)) return '';
  // 仍以纯英文 key 开头则丢弃
  if (/^[A-Za-z][A-Za-z0-9_.[\]]*\s*=/.test(s)) return '';
  // 无中文数字比例碎片
  if (!/[\u4e00-\u9fff]/.test(s) && /^[\d.\s/／、,，-]+$/.test(s)) return '';
  // 「袁军成、袁军成（日期）」去重（不动金额 4,430,000）
  s = s.replace(
    /([\u4e00-\u9fff]{2,8})([、，])\1(?=(?:[、，]|[（(]|$|[；;]))/gu,
    '$1',
  );
  return s;
}

/**
 * 线索判断：人读化字段码；卡片只保留主判断（去掉替代解释/置信度尾巴）。
 */
export function cleanV3Inference(text: string | null | undefined, maxLen = 180): string {
  if (!text) return '';
  if (isV3ComposeMetaText(text)) return '';
  let s = stripNoiseParens(String(text).trim());
  s = humanizeV3InlineText(s);
  if (isV3ComposeMetaText(s)) return '';

  // 若含「推断：」优先取推断段
  const infer = s.match(/推断[:：]\s*([^；;]+(?:；[^；;]+)?)/);
  if (infer?.[1]) {
    s = infer[1].trim();
  } else {
    // 去掉尾部 替代解释 / 置信度 / 解释
    s = s
      .replace(/[；;，,]?\s*替代解释[:：][\s\S]*$/u, '')
      .replace(/[；;，,]?\s*解释[:：][\s\S]*$/u, '')
      .replace(/[；;，,]?\s*置信度[:：]?[\s\S]*$/u, '')
      .replace(/[（(]\s*前提[:：][\s\S]*$/u, '')
      .trim();
    // 若以「前提：」开头且后面还有正文，去掉前提标签保留叙述
    s = s.replace(/^前提[:：]\s*/u, '').trim();
  }

  // 残缺括号尾巴：置信度中) / …
  s = s.replace(/[（(]?置信度[^）)]*$/u, '').replace(/[）)]+$/g, '').trim();
  s = s.replace(/[；;，,。.]+$/g, '').trim();

  if (s.length > maxLen) {
    const cut = s.slice(0, maxLen);
    const last = Math.max(
      cut.lastIndexOf('；'),
      cut.lastIndexOf('。'),
      cut.lastIndexOf('，'),
      cut.lastIndexOf('（'),
      cut.lastIndexOf('('),
    );
    // 优先在句读处截；若只能切到开括号前，避免半截括号
    s = `${(last > 40 ? cut.slice(0, last) : cut).replace(/[；;，,。.]+$/, '')}…`;
    s = trimUnbalancedParens(s.replace(/…$/g, '')).trim();
    if (s && !s.endsWith('…')) s = `${s}…`;
  } else {
    s = trimUnbalancedParens(s);
  }
  if (isV3ComposeMetaText(s)) return '';
  return s;
}

function maturityOf(lead: V3Lead): PreliminaryMaturity {
  const raw = String(lead.maturity || lead.priority || '').trim().toUpperCase();
  const letter = raw.slice(0, 1);
  if (letter === 'A' || letter === 'B' || letter === 'C' || letter === 'D') return letter;
  const m = Number(lead.m_score);
  const p = Number(lead.p_score);
  if (Number.isFinite(m) && m >= 0.75) return 'A';
  if (Number.isFinite(p) && p >= 0.7) return 'A';
  if (Number.isFinite(m) && m >= 0.5) return 'B';
  if (Number.isFinite(p) && p >= 0.45) return 'B';
  if (Number.isFinite(m) && m >= 0.3) return 'C';
  return 'C';
}

function asStringList(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v.map((x) => String(x ?? '').trim()).filter(Boolean);
}

function interviewQuestions(lead: V3Lead): string[] {
  const qs = lead.interview_questions;
  if (!Array.isArray(qs)) return [];
  return qs
    .map((item) => {
      if (typeof item === 'string') return item.trim();
      if (item && typeof item === 'object' && 'q' in item) {
        return String((item as { q?: unknown }).q ?? '').trim();
      }
      return '';
    })
    .filter(Boolean);
}

function uniqueNonEmpty(items: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const item of items) {
    const t = item.trim();
    if (t.length < 2 || seen.has(t)) continue;
    seen.add(t);
    out.push(t);
  }
  return out;
}

function asIdList(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v.map((x) => String(x ?? '').trim()).filter(Boolean);
}

/**
 * 同主题折叠键：把近义 atomic 线索收到同一桶（旗峰样本：注册资本/进出口/存货等各有多条）。
 * composite 用精确标题，避免误并。
 */
function topicFoldKey(lead: V3Lead): string {
  const title = String(lead.title || '').trim();
  const layer = String(lead.origin_layer || '').toLowerCase();
  if (layer === 'composite') return `composite:${title}`;

  if (/注册资本|实缴出资|章程修订|交割/.test(title)) return 'topic:reg_capital';
  if (/进出口|外贸|海关|出口退|对外贸易/.test(title)) return 'topic:trade';
  if (/存货|成本结转|产销链|收发存/.test(title)) return 'topic:inventory';
  if (/从业|所得税优惠|优惠适用|加计/.test(title)) return 'topic:eit_incentive';
  if (/年报|年度报告|公示义务/.test(title)) return 'topic:annual_report';
  if (/信用代码|属地码|登记地|登记区域/.test(title)) return 'topic:uscc_region';
  if (/消防|行政处罚|司法涉诉/.test(title)) return 'topic:penalty';
  if (/开票|税率|征收率|收入分类|免税零税率/.test(title)) return 'topic:invoice';
  if (/合同模板|交易规则/.test(title)) return 'topic:contract_template';
  if (/许可联动|备案与许可|许可适用|经营范围新增/.test(title)) return 'topic:license';
  if (/跨主体|关联主体|族群|持股比例|服务机会核验/.test(title)) {
    return 'topic:cross_entity';
  }
  // 精确键用清洗后标题，避免 ENT-xxx 前缀导致同主题多条
  return `exact:${cleanLeadDisplayTitle(title)}`;
}

/**
 * 结果页展示用线索子集。顺序以入参数组为准（后端已按优先级、事件日排好）。
 * 1) 去掉已被 composite 引用为 parent 的 atomic
 * 2) 同标题 / 同主题只留数组中先出现的一条
 */
export function selectDisplayV3Leads(
  active: V3Lead[] | undefined | null,
): V3Lead[] {
  const list = Array.isArray(active) ? active : [];
  if (list.length <= 1) return list;

  const parentIds = new Set<string>();
  for (const lead of list) {
    if (String(lead.origin_layer || '').toLowerCase() !== 'composite') continue;
    for (const id of asIdList(lead.parent_lead_ids)) parentIds.add(id);
  }

  const seenTitle = new Set<string>();
  const seenTopic = new Set<string>();
  const out: V3Lead[] = [];
  for (const lead of list) {
    const id = String(lead.lead_id || lead.id || '');
    const layer = String(lead.origin_layer || '').toLowerCase();
    if (layer !== 'composite' && id && parentIds.has(id)) continue;
    const title = cleanLeadDisplayTitle(String(lead.title || '')) || id;
    if (seenTitle.has(title)) continue;
    const topic = topicFoldKey(lead);
    if (seenTopic.has(topic)) continue;
    seenTitle.add(title);
    seenTopic.add(topic);
    out.push(lead);
  }
  return out;
}

/**
 * 聚合父线索的公开事实 / 推断。
 * 库内实测：composite 的 public_facts/inferences 常是「组合自 N 条父线索」模板，
 * 真实业务句在 parent_lead_ids 指向的 atomic 上。
 */
function collectParentFactsAndInferences(
  lead: V3Lead,
  byId: Map<string, V3Lead>,
): { facts: string[]; inferences: string[] } {
  const facts: string[] = [];
  const inferences: string[] = [];
  for (const pid of asIdList(lead.parent_lead_ids)) {
    const parent = byId.get(pid);
    if (!parent) continue;
    for (const f of asStringList(parent.public_facts)) {
      const cleaned = cleanV3PublicFact(f);
      if (cleaned) facts.push(cleaned);
    }
    for (const inf of asStringList(parent.inferences)) {
      const cleaned = cleanV3Inference(inf, 220);
      if (cleaned.length >= 4) inferences.push(cleaned);
    }
  }
  return {
    facts: uniqueNonEmpty(facts),
    inferences: uniqueNonEmpty(inferences),
  };
}

function isCompositeLead(lead: V3Lead): boolean {
  if (String(lead.origin_layer || '').toLowerCase() === 'composite') return true;
  // 有父线索且自身 facts/inferences 是组合模板时，也按 composite 处理
  if (asIdList(lead.parent_lead_ids).length === 0) return false;
  const facts = asStringList(lead.public_facts);
  const infs = asStringList(lead.inferences);
  return (
    facts.some((f) => isV3ComposeMetaText(f)) ||
    infs.some((f) => isV3ComposeMetaText(f))
  );
}

/** 模型三接口投影的线索：不折叠、不补写待核验句。池并入条目 source/origin=pool。 */
export function projectedActiveToPreliminaryLeads(
  active: V3Lead[] | undefined | null,
  companyName: string,
): PreliminaryLead[] {
  const today = new Date().toISOString().slice(0, 10);
  return (Array.isArray(active) ? active : []).map((raw, index) => {
    const display = raw.display;
    const fromPool =
      raw.source === 'pool' ||
      String((display as { origin?: string } | undefined)?.origin || '') === 'pool';
    const letter = String(raw.maturity || raw.priority || '')
      .trim()
      .toUpperCase()
      .slice(0, 1);
    const maturityLevel: PreliminaryMaturity =
      letter === 'A' || letter === 'B' || letter === 'C' || letter === 'D' ? letter : 'C';
    const priority = String(display?.priorityLabel || '').trim();
    const facts = (display?.knownFacts || []).map((item) => String(item || '').trim()).filter(Boolean);
    const questions = (display?.toVerify || []).map((item) => String(item || '').trim()).filter(Boolean);
    const judgment = String(display?.judgment || '').trim();
    const trigger = String(display?.trigger || '').trim() || '未标注触发事件';
    const taxTypes = (raw.tax_types || []).map((item) => String(item || '').trim()).filter(Boolean);
    const title = String(raw.title || '').trim() || `线索 ${index + 1}`;
    const eventDate = String(display?.eventDate || '').trim();
    const note =
      priority === '高'
        ? '优先跟进'
        : priority === '中'
          ? '建议核验'
          : priority === '低'
            ? '观察信号'
            : '待评估';
    const services = (display?.suggestedServices || [])
      .map((item) => ({
        category: String(item?.category || '').trim(),
        content: String(item?.content || '').trim(),
      }))
      .filter((item) => item.category || item.content);
    const sources = (display?.evidenceSources || [])
      .map((item) => ({
        name: String(item?.name || '').trim(),
        type: String(item?.type || '').trim(),
        date: String(item?.date || '').trim(),
        note: String(item?.note || '').trim(),
      }))
      .filter((item) => item.name || item.note);
    const nextActions = (display?.nextActions || [])
      .map((item) => String(item || '').trim())
      .filter(Boolean);
    return {
      id: String(raw.lead_id || raw.id || `L-${index + 1}`),
      enterpriseName: companyName,
      leadType: String(raw.lead_type || '其他').trim() || '其他',
      previewTitle: title,
      previewSummary: (judgment || facts[0] || title).slice(0, 160),
      eventDate: eventDate || '—',
      triggerSignals: [trigger],
      potentialImpact: [],
      possibleTaxTypes: taxTypes,
      evidenceSummary: facts.join('；') || '未标注已知事实',
      maturityLevel,
      maturityNote: note,
      verificationGaps: questions,
      recommendedAction: maturityLevel === 'A' || maturityLevel === 'B' ? '补充资料' : '查看详情',
      updatedAt: today,
      recommendedMaterials: [],
      verifyStatus: String(display?.verifyStatus || '').trim() || undefined,
      evidenceLevel: String(display?.evidenceLevel || '').trim() || undefined,
      priorityLabel: priority || undefined,
      judgmentReason: String(display?.judgmentReason || '').trim() || undefined,
      publishDate: String(display?.publishDate || '').trim() || undefined,
      nextActions: nextActions.length ? nextActions : undefined,
      suggestedServices: services.length ? services : undefined,
      evidenceSources: sources.length ? sources : undefined,
      detail: {
        sourceName: fromPool ? '潜在线索池' : '模型线索',
        sourceDate: eventDate,
        verifiedFacts: facts,
        professionalJudgment: judgment,
        possibleNormalExplanation: '',
        missingConditions: questions,
        applicablePolicyName: '',
        upgradeSteps: [],
      },
    };
  });
}

/** v3 active[] → 结果页 PreliminaryLead */
export function v3ActiveToPreliminaryLeads(
  active: V3Lead[] | undefined | null,
  companyName: string,
): PreliminaryLead[] {
  const allActive = Array.isArray(active) ? active : [];
  const byId = new Map<string, V3Lead>();
  for (const lead of allActive) {
    const lid = String(lead.lead_id || lead.id || '');
    if (lid) byId.set(lid, lead);
  }
  const list = selectDisplayV3Leads(allActive);
  const updatedAt = new Date().toISOString().slice(0, 10);
  return list.map((raw, index) => {
    const id = String(raw.lead_id || raw.id || `L-V3-${index + 1}`);
    const title = cleanLeadDisplayTitle(raw.title);
    const leadType = normalizeLeadType(String(raw.lead_type || '其他'));
    const maturityLevel = maturityOf(raw);
    const beDisplay = raw.display;
    const hasBeDisplay = Boolean(
      beDisplay &&
        (beDisplay.trigger || beDisplay.judgment || (beDisplay.knownFacts?.length ?? 0) > 0),
    );

    let facts = uniqueNonEmpty(asStringList(raw.public_facts).map(cleanV3PublicFact));
    let inferences = asStringList(raw.inferences)
      .map((s) => cleanV3Inference(s, 220))
      .filter((s) => s.length >= 4);
    // composite：一律用父线索正文（自身非空的「标题摘要」也不能抢触发事件）
    if (!hasBeDisplay && isCompositeLead(raw)) {
      const fromParents = collectParentFactsAndInferences(raw, byId);
      if (fromParents.facts.length) facts = fromParents.facts;
      if (fromParents.inferences.length) inferences = fromParents.inferences;
    }
    const unknowns = uniqueNonEmpty(
      asStringList(raw.unknowns).map((s) => {
        const cleaned = cleanV3PublicFact(s) || humanizeV3InlineText(s);
        return cleaned || cleanEvidenceAnnotations(s) || '';
      }),
    );
    const questions = uniqueNonEmpty(
      interviewQuestions(raw).map((s) => {
        const cleaned = cleanV3PublicFact(s) || humanizeV3InlineText(s);
        return cleaned || cleanEvidenceAnnotations(s) || s.trim();
      }),
    );
    const gapsFromBe = (beDisplay?.toVerify || [])
      .map((g) => humanizeV3InlineText(String(g || '').trim()))
      .map((g) => g.replace(/^[、，,；;\s]+|[、，,；;\s]+$/g, '').trim())
      .filter((g) => g.length >= 2 && /[\u4e00-\u9fff]/.test(g));
    const gaps = (
      gapsFromBe.length
        ? gapsFromBe
        : (unknowns.length ? unknowns : questions)
            .map((g) => humanizeV3InlineText(g))
            .map((g) => g.replace(/^[、，,；;\s]+|[、，,；;\s]+$/g, '').trim())
            .filter((g) => g.length >= 2 && /[\u4e00-\u9fff]/.test(g))
    ).slice(0, 3);
    const titleKey = normalizeCompareText(title);
    const distinctInference =
      inferences.find((s) => normalizeCompareText(s) !== titleKey) || '';
    // 判断只来自推断（或后端 display.judgment）；禁止用 public_facts/事件句顶替
    let judgment = hasBeDisplay
      ? humanizeV3InlineText(String(beDisplay?.judgment || ''))
      : distinctInference;
    if (!judgment || normalizeCompareText(judgment) === titleKey) {
      judgment = distinctInference || '';
    }
    // 触发事件：优先后端 display；否则本地挑选（禁止裸电话/数字碎片）
    const triggerFromBe = hasBeDisplay
      ? humanizeV3InlineText(String(beDisplay?.trigger || ''))
      : '';
    const triggerCandidate =
      (triggerFromBe && !isUnsuitableTriggerFact(triggerFromBe) ? triggerFromBe : '') ||
      pickTriggerFact(facts);
    const looksLikeTitleWrappedTrigger = (s: string) =>
      /^公开(?:信息|年报[/／]?登记信息)?触发[「『]/.test(s.trim());
    const trigger =
      (triggerCandidate &&
      normalizeCompareText(triggerCandidate) !== titleKey &&
      !looksLikeTitleWrappedTrigger(triggerCandidate) &&
      triggerCandidate !== SOFT_TRIGGER_NO_EVENT
        ? triggerCandidate
        : '') || SOFT_TRIGGER_NO_EVENT;

    // 已知事实：逐条清洗；保留带标签电话，丢弃纯数字比例；禁止回退到触发软提示
    const knownFromBe = hasBeDisplay
      ? (beDisplay?.knownFacts || [])
          .map((f) => cleanV3PublicFact(String(f || '')))
          .filter((f) => f.length >= 2)
      : [];
    // 后端已经给出 knownFacts（含空数组）时不再用原始 public_facts 回填
    const knownSource =
      hasBeDisplay && Array.isArray(beDisplay?.knownFacts) ? knownFromBe : facts;
    const triggerKey = normalizeCompareText(trigger);
    const knownList = uniqueNonEmpty(
      knownSource.filter((f) => {
        if (!/[\u4e00-\u9fff]/.test(f)) return false;
        if (isStandingAttributeFact(f)) return false;
        if (looksLikeTitleWrappedTrigger(f)) return false;
        if (f === SOFT_TRIGGER_NO_EVENT || f === SOFT_KNOWN_FACTS_EMPTY) return false;
        // 与触发事件同句不得再占「已知事实」
        if (triggerKey && normalizeCompareText(f) === triggerKey) return false;
        // 允许「公示电话：…」；拒绝无标签数字堆
        if (/^[\d.\s/／、,，-]+$/.test(f)) return false;
        return true;
      }),
    ).slice(0, 4);
    // 判断不得与触发同句（无推断时留空，卡片再用标题，禁止用事件事实顶替）
    if (judgment && triggerKey && normalizeCompareText(judgment) === triggerKey) {
      judgment = distinctInference || '';
    }
    const knownFacts = knownList.join('；') || SOFT_KNOWN_FACTS_EMPTY;
    const taxTypes = asStringList(raw.tax_types).slice(0, 3);
    const period = String(raw.period || updatedAt).slice(0, 10);
    // 事件发生时间：后端 display.eventDate → 触发/标题抽取；禁用 period 顶替
    const eventDate =
      (hasBeDisplay && beDisplay?.eventDate) ||
      extractEventDate(trigger, title, ...facts) ||
      period;
    const recommendedMaterials = materialsFromMissingEvidence(
      asStringList(raw.missing_evidence),
      title,
      period,
      maturityLevel,
    );
    const missingConditions = (
      recommendedMaterials.length
        ? recommendedMaterials.map((m) => m.name)
        : asStringList(raw.missing_evidence)
            .map((s) => cleanV3PublicFact(s) || humanizeV3InlineText(s) || s.trim())
            .filter(Boolean)
    ).slice(0, 8);

    return {
      id,
      enterpriseName: companyName,
      leadType,
      previewTitle: title,
      previewSummary: (judgment || knownFacts || `围绕「${title}」的公开信息核验`).slice(
        0,
        160,
      ),
      eventDate,
      triggerSignals: [trigger],
      potentialImpact:
        leadType === '税收优惠'
          ? ['税负', '现金流']
          : leadType === '税务风险'
            ? ['合规', '现金流']
            : ['合规'],
      possibleTaxTypes: taxTypes.length ? taxTypes : ['企业所得税'],
      evidenceSummary: knownFacts,
      maturityLevel,
      maturityNote:
        maturityLevel === 'A'
          ? '优先跟进'
          : maturityLevel === 'B'
            ? '建议核验'
            : maturityLevel === 'C'
              ? '观察信号'
              : '待评估',
      verificationGaps: gaps.length
        ? gaps
        : ['相关原始凭证与业务实质需核实', '申报口径与账面数据需逐期比对'],
      recommendedAction:
        maturityLevel === 'A' || maturityLevel === 'B' ? '补充资料' : '查看详情',
      updatedAt,
      recommendedMaterials,
      detail: {
        sourceName: '公开信息整理',
        sourceDate: updatedAt,
        verifiedFacts: facts.length ? facts : [trigger],
        professionalJudgment: judgment,
        possibleNormalExplanation:
          '部分信号可能源于公开口径差异，未必构成实质性结论，需调取内部资料确认。',
        missingConditions,
        applicablePolicyName: asStringList(raw.rule_ids)[0] || '待匹配适用政策',
        upgradeSteps: [
          {
            key: 'data',
            label: '资料补充',
            description: `补齐${leadType}相关的原始凭证与证明材料`,
          },
        ],
      },
    };
  });
}

export function emptyV3Leads(): V3LeadsPayload {
  return { active: [], excluded: [], total: 0 };
}

export function normalizeV3Leads(raw: unknown): V3LeadsPayload {
  if (!raw || typeof raw !== 'object') return emptyV3Leads();
  const obj = raw as Record<string, unknown>;
  const active = Array.isArray(obj.active)
    ? (obj.active.filter((x) => x && typeof x === 'object') as V3Lead[])
    : [];
  const excluded = Array.isArray(obj.excluded)
    ? (obj.excluded.filter((x) => x && typeof x === 'object') as V3Lead[])
    : [];
  const total =
    typeof obj.total === 'number' ? obj.total : active.length + excluded.length;
  const projected = obj.projected === true;
  const excludedCount =
    typeof obj.excludedCount === 'number' ? obj.excludedCount : undefined;
  return {
    active,
    excluded,
    total,
    ...(projected ? { projected: true as const } : {}),
    ...(excludedCount != null ? { excludedCount } : {}),
  };
}
